import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Avatar,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Snackbar,
  Alert,
  Divider,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  LocalShipping as ShippingIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Receipt as ReceiptIcon,
  AttachMoney as MoneyIcon,
  Assignment as AssignmentIcon,
  AttachFile as AttachFileIcon,
  DeleteOutline as DeleteOutlineIcon,
} from '@mui/icons-material';
import { useAuth } from '@contexts/AuthContext';
import { formatDateOnly } from '@utils/dateFormatter';
import { API_ORIGIN } from '@config/api';
import { inventoryService } from '@services/inventoryService';
import {
  fetchPurchaseOrders,
  createPurchaseOrder,
  updatePurchaseOrderDraft,
  sendPurchaseOrder,
  cancelPurchaseOrder,
  receivePurchaseOrder,
  fetchPurchaseOrderAttachments,
  uploadPurchaseOrderAttachment,
  deletePurchaseOrderAttachment,
  purchaseOrderServiceErrorMessage,
  type PurchaseOrderDto,
  type PoLineInput,
  type ReceiveLineInput,
  type PurchaseOrderAttachmentDto,
} from '@services/purchaseOrdersService';
import { fetchSuppliers, createSupplier, type SupplierDto } from '@services/suppliersService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} id={`purchase-orders-tabpanel-${index}`} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface ProductOpt {
  id: string;
  name: string;
}

const emptyLine = (): PoLineInput => ({
  productId: '',
  quantityOrdered: 1,
  unitPrice: 0,
});

const PurchaseOrdersManagement: React.FC = () => {
  const { user } = useAuth();
  const clinicId = user?.clinicId;

  const [loading, setLoading] = useState(true);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrderDto[]>([]);
  const [suppliers, setSuppliers] = useState<SupplierDto[]>([]);
  const [products, setProducts] = useState<ProductOpt[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedSupplier, setSelectedSupplier] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [snack, setSnack] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [supplierQuickOpen, setSupplierQuickOpen] = useState(false);
  const [newSupplierName, setNewSupplierName] = useState('');
  const [newSupplierPhone, setNewSupplierPhone] = useState('');
  const [newSupplierEmail, setNewSupplierEmail] = useState('');

  const [formSupplierId, setFormSupplierId] = useState('');
  const [formLocation, setFormLocation] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [formLines, setFormLines] = useState<PoLineInput[]>([emptyLine()]);

  const [editOrder, setEditOrder] = useState<PurchaseOrderDto | null>(null);
  const [viewOrder, setViewOrder] = useState<PurchaseOrderDto | null>(null);
  const [receiveOrder, setReceiveOrder] = useState<PurchaseOrderDto | null>(null);
  const [receiveLines, setReceiveLines] = useState<Record<string, { qty: string; batch: string; exp: string; cost: string }>>(
    {}
  );

  const [poAttachments, setPoAttachments] = useState<PurchaseOrderAttachmentDto[]>([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const attachmentFileRef = useRef<HTMLInputElement | null>(null);

  const showError = (message: string) => setSnack({ open: true, message, severity: 'error' });
  const showOk = (message: string) => setSnack({ open: true, message, severity: 'success' });

  const loadAll = useCallback(async () => {
    if (!clinicId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [ords, sups, inv] = await Promise.all([
        fetchPurchaseOrders(clinicId),
        fetchSuppliers(clinicId),
        inventoryService.getInventoryList({ clinicId, limit: 500, page: 1 }),
      ]);
      setPurchaseOrders(ords);
      setSuppliers(sups);
      setProducts(
        (inv.items || []).map((r: Record<string, unknown>) => ({
          id: String(r.id),
          name: String(r.name || ''),
        }))
      );
    } catch (e) {
      showError(purchaseOrderServiceErrorMessage(e));
        setPurchaseOrders([]);
      setSuppliers([]);
      setProducts([]);
      } finally {
        setLoading(false);
      }
  }, [clinicId]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (!clinicId || !viewOrder) {
      setPoAttachments([]);
      return;
    }
    let cancelled = false;
    setAttachmentsLoading(true);
    void fetchPurchaseOrderAttachments(clinicId, viewOrder.id)
      .then((list) => {
        if (!cancelled) setPoAttachments(list);
      })
      .catch((e) => {
        if (!cancelled) {
          showError(purchaseOrderServiceErrorMessage(e));
          setPoAttachments([]);
        }
      })
      .finally(() => {
        if (!cancelled) setAttachmentsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clinicId, viewOrder]);

  const resolveAttachmentHref = (a: PurchaseOrderAttachmentDto): string => {
    if (a.url.startsWith('http')) return a.url;
    return `${API_ORIGIN}${a.url.startsWith('/') ? a.url : `/${a.url}`}`;
  };

  const onPickAttachment = () => attachmentFileRef.current?.click();

  const onAttachmentFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !clinicId || !viewOrder) return;
    if (viewOrder.status === 'cancelled') {
      showError('Nu poti incarca atasamente la o comanda anulata');
      return;
    }
    try {
      await uploadPurchaseOrderAttachment(clinicId, viewOrder.id, file);
      showOk('Atasament incarcat');
      const list = await fetchPurchaseOrderAttachments(clinicId, viewOrder.id);
      setPoAttachments(list);
    } catch (err) {
      showError(purchaseOrderServiceErrorMessage(err));
    }
  };

  const onDeleteAttachment = async (attachmentId: string) => {
    if (!clinicId || !viewOrder) return;
    if (!window.confirm('Stergi atasamentul?')) return;
    try {
      await deletePurchaseOrderAttachment(clinicId, viewOrder.id, attachmentId);
      showOk('Atasament sters');
      setPoAttachments((prev) => prev.filter((x) => x.id !== attachmentId));
    } catch (err) {
      showError(purchaseOrderServiceErrorMessage(err));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'default';
      case 'sent':
        return 'info';
      case 'partially_received':
        return 'warning';
      case 'received':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'draft':
        return 'Draft';
      case 'sent':
        return 'Trimisa';
      case 'partially_received':
        return 'Partial receptionata';
      case 'received':
        return 'Receptionata';
      case 'cancelled':
        return 'Anulata';
      default:
        return status;
    }
  };

  const filteredPurchaseOrders = purchaseOrders.filter((order) => {
    const q = searchTerm.toLowerCase();
    const matchesSearch =
      order.orderNumber.toLowerCase().includes(q) ||
      order.supplierName.toLowerCase().includes(q) ||
      order.id.toLowerCase().includes(q);
    const matchesStatus = selectedStatus === 'all' || order.status === selectedStatus;
    const matchesSupplier = selectedSupplier === 'all' || order.supplierId === selectedSupplier;
    return matchesSearch && matchesStatus && matchesSupplier;
  });

  const draftOrders = filteredPurchaseOrders.filter((o) => o.status === 'draft');
  const sentOrders = filteredPurchaseOrders.filter((o) => o.status === 'sent');
  const partialOrders = filteredPurchaseOrders.filter((o) => o.status === 'partially_received');
  const receivedOrders = filteredPurchaseOrders.filter((o) => o.status === 'received');
  const allOrders = filteredPurchaseOrders;

  const openCreate = () => {
    setFormSupplierId(suppliers[0]?.id || '');
    setFormLocation('');
    setFormNotes('');
    setFormLines([emptyLine()]);
    setCreateOpen(true);
  };

  const submitCreate = async () => {
    if (!clinicId) return;
    if (!formSupplierId) {
      showError('Selecteaza furnizorul');
      return;
    }
    const lines = formLines.filter((l) => l.productId && l.quantityOrdered > 0);
    if (!lines.length) {
      showError('Adauga cel putin un produs');
      return;
    }
    try {
      await createPurchaseOrder(clinicId, {
        supplierId: formSupplierId,
        locationName: formLocation || undefined,
        notes: formNotes || undefined,
        items: lines,
      });
      showOk('Comanda a fost creata');
      setCreateOpen(false);
      await loadAll();
    } catch (e) {
      showError(purchaseOrderServiceErrorMessage(e));
    }
  };

  const openEdit = (o: PurchaseOrderDto) => {
    setEditOrder(o);
    setFormSupplierId(o.supplierId);
    setFormLocation(o.locationName || '');
    setFormNotes(o.notes || '');
    setFormLines(
      o.items.length
        ? o.items.map((i) => ({
            productId: i.productId,
            quantityOrdered: i.quantityOrdered,
            unitPrice: i.unitPrice,
          }))
        : [emptyLine()]
    );
  };

  const submitEdit = async () => {
    if (!clinicId || !editOrder) return;
    const lines = formLines.filter((l) => l.productId && l.quantityOrdered > 0);
    if (!lines.length) {
      showError('Adauga cel putin un produs');
      return;
    }
    try {
      await updatePurchaseOrderDraft(clinicId, editOrder.id, {
        supplierId: formSupplierId,
        locationName: formLocation || undefined,
        notes: formNotes || undefined,
        items: lines,
      });
      showOk('Comanda actualizata');
      setEditOrder(null);
      await loadAll();
    } catch (e) {
      showError(purchaseOrderServiceErrorMessage(e));
    }
  };

  const handleSend = async (o: PurchaseOrderDto) => {
    if (!clinicId) return;
    try {
      await sendPurchaseOrder(clinicId, o.id);
      showOk('Comanda marcata ca trimisa');
      await loadAll();
    } catch (e) {
      showError(purchaseOrderServiceErrorMessage(e));
    }
  };

  const handleCancel = async (o: PurchaseOrderDto) => {
    if (!clinicId) return;
    if (!window.confirm(`Anulezi comanda ${o.orderNumber}?`)) return;
    try {
      await cancelPurchaseOrder(clinicId, o.id);
      showOk('Comanda anulata');
      await loadAll();
    } catch (e) {
      showError(purchaseOrderServiceErrorMessage(e));
    }
  };

  const openReceive = (o: PurchaseOrderDto) => {
    const init: Record<string, { qty: string; batch: string; exp: string; cost: string }> = {};
    for (const it of o.items) {
      const rem = Math.max(0, it.quantityOrdered - it.quantityReceived);
      if (rem > 0) {
        init[it.id] = {
          qty: String(rem),
          batch: '',
          exp: '',
          cost: String(it.unitPrice),
        };
      }
    }
    setReceiveLines(init);
    setReceiveOrder(o);
  };

  const submitReceive = async () => {
    if (!clinicId || !receiveOrder) return;
    const lines: ReceiveLineInput[] = [];
    for (const it of receiveOrder.items) {
      const st = receiveLines[it.id];
      if (!st) continue;
      const q = parseFloat(st.qty);
      if (!Number.isFinite(q) || q <= 0) continue;
      lines.push({
        purchaseOrderItemId: it.id,
        quantity: q,
        batchNumber: st.batch.trim(),
        expirationDate: st.exp.trim(),
        unitCost: parseFloat(st.cost) || 0,
      });
    }
    if (!lines.length) {
      showError('Completeaza cel putin o linie de receptie');
      return;
    }
    for (const l of lines) {
      if (!l.batchNumber) {
        showError('Numar lot obligatoriu pentru fiecare linie');
        return;
      }
      if (!l.expirationDate || l.expirationDate.length < 8) {
        showError('Data expirarii obligatorie (YYYY-MM-DD)');
        return;
      }
    }
    try {
      await receivePurchaseOrder(clinicId, receiveOrder.id, lines);
      showOk('Receptie inregistrata');
      setReceiveOrder(null);
      await loadAll();
    } catch (e) {
      showError(purchaseOrderServiceErrorMessage(e));
    }
  };

  const submitQuickSupplier = async () => {
    if (!clinicId || !newSupplierName.trim()) {
      showError('Numele furnizorului este obligatoriu');
      return;
    }
    try {
      const s = await createSupplier(clinicId, {
        name: newSupplierName.trim(),
        phone: newSupplierPhone || undefined,
        email: newSupplierEmail || undefined,
      });
      showOk('Furnizor adaugat');
      setSupplierQuickOpen(false);
      setNewSupplierName('');
      setNewSupplierPhone('');
      setNewSupplierEmail('');
      await loadAll();
      setFormSupplierId(s.id);
    } catch (e) {
      showError(purchaseOrderServiceErrorMessage(e));
    }
  };

  const addFormLine = () => setFormLines((prev) => [...prev, emptyLine()]);
  const removeFormLine = (idx: number) => setFormLines((prev) => prev.filter((_, i) => i !== idx));
  const setLine = (idx: number, patch: Partial<PoLineInput>) =>
    setFormLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

  const renderTable = (rows: PurchaseOrderDto[]) => (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Comanda</TableCell>
              <TableCell>Furnizor</TableCell>
              <TableCell>Locatie</TableCell>
              <TableCell>Produse</TableCell>
              <TableCell>Valoare</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actiuni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((order) => (
              <TableRow key={order.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'grey.300' }}>
                      <AssignmentIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {order.orderNumber}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDateOnly(order.createdAt)}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{order.supplierName}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{order.locationName || '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{order.items.length} produse</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {order.items.map((item) => item.productName).join(', ')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {order.totalAmount.toFixed(2)} RON
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={getStatusLabel(order.status)}
                    color={getStatusColor(order.status) as 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    <Tooltip title="Detalii">
                      <IconButton size="small" onClick={() => setViewOrder(order)}>
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    {order.status === 'draft' && (
                      <>
                        <Tooltip title="Editeaza">
                          <IconButton size="small" onClick={() => openEdit(order)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Trimite">
                          <IconButton size="small" onClick={() => void handleSend(order)}>
                            <ShippingIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Anuleaza">
                          <IconButton size="small" onClick={() => void handleCancel(order)}>
                            <CancelIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {(order.status === 'sent' || order.status === 'partially_received') && (
                      <>
                        <Tooltip title="Receptioneaza">
                          <IconButton size="small" onClick={() => openReceive(order)}>
                            <CheckIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Anuleaza">
                          <IconButton size="small" onClick={() => void handleCancel(order)}>
                            <CancelIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {order.status === 'received' && (
                      <Tooltip title="Detalii receptie">
                        <IconButton size="small" onClick={() => setViewOrder(order)}>
                          <ReceiptIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </>
  );

  if (!clinicId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Nu exista clinicId pentru utilizatorul curent. Comenzile de achizitie sunt disponibile pentru personalul clinicii.</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Se incarca comenzile...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Snackbar open={snack.open} autoHideDuration={6000} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Achizitii & Comenzi Furnizori
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Comenzi reale catre furnizori, trimite, receptioneaza pe loturi, atasamente documente
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ borderRadius: 2 }}>
          Comanda Noua
        </Button>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.light' }}>
                  <AssignmentIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    {allOrders.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Comenzi Totale
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'warning.light' }}>
                  <ScheduleIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    {sentOrders.length + partialOrders.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    In curs (trimise / partial)
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'success.light' }}>
                  <CheckIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    {receivedOrders.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Receptionate
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'info.light' }}>
                  <MoneyIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    {allOrders.reduce((sum, order) => sum + order.totalAmount, 0).toFixed(0)} RON
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Valoare Totala
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                placeholder="Cauta comanda sau furnizor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} label="Status">
                  <MenuItem value="all">Toate</MenuItem>
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="sent">Trimisa</MenuItem>
                  <MenuItem value="partially_received">Partial receptionata</MenuItem>
                  <MenuItem value="received">Receptionata</MenuItem>
                  <MenuItem value="cancelled">Anulata</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Furnizor</InputLabel>
                <Select value={selectedSupplier} onChange={(e) => setSelectedSupplier(e.target.value)} label="Furnizor">
                  <MenuItem value="all">Toti</MenuItem>
                  {suppliers.map((supplier) => (
                    <MenuItem key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Tooltip title="Reincarca">
                <IconButton onClick={() => void loadAll()} sx={{ height: 56 }}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
            <Tab label={`Draft (${draftOrders.length})`} />
            <Tab label={`Trimise (${sentOrders.length})`} />
            <Tab label={`Partial (${partialOrders.length})`} />
            <Tab label={`Receptionate (${receivedOrders.length})`} />
            <Tab label={`Toate (${allOrders.length})`} />
          </Tabs>
        </Box>
        <TabPanel value={tabValue} index={0}>
          {renderTable(draftOrders)}
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          {renderTable(sentOrders)}
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          {renderTable(partialOrders)}
        </TabPanel>
        <TabPanel value={tabValue} index={3}>
          {renderTable(receivedOrders)}
        </TabPanel>
        <TabPanel value={tabValue} index={4}>
          {renderTable(allOrders)}
        </TabPanel>
      </Card>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Comanda noua</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Furnizor</InputLabel>
              <Select value={formSupplierId} label="Furnizor" onChange={(e) => setFormSupplierId(e.target.value)}>
                {suppliers.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="outlined" onClick={() => setSupplierQuickOpen(true)}>
              Furnizor nou
            </Button>
                          </Box>
          <TextField
            fullWidth
            label="Locatie / depozit (optional)"
            value={formLocation}
            onChange={(e) => setFormLocation(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField fullWidth label="Note" value={formNotes} onChange={(e) => setFormNotes(e.target.value)} multiline minRows={2} sx={{ mb: 2 }} />
          <Typography variant="subtitle2" gutterBottom>
            Linii (din inventarul clinicii)
                        </Typography>
          {formLines.map((line, idx) => (
            <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <FormControl sx={{ minWidth: 220 }} size="small">
                <InputLabel>Produs</InputLabel>
                <Select
                  label="Produs"
                  value={line.productId}
                  onChange={(e) => setLine(idx, { productId: e.target.value })}
                >
                  {products.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                          size="small"
                label="Cantitate"
                type="number"
                value={line.quantityOrdered}
                onChange={(e) => setLine(idx, { quantityOrdered: parseFloat(e.target.value) || 0 })}
                sx={{ width: 110 }}
              />
              <TextField
                size="small"
                label="Pret unitar"
                type="number"
                value={line.unitPrice}
                onChange={(e) => setLine(idx, { unitPrice: parseFloat(e.target.value) || 0 })}
                sx={{ width: 120 }}
              />
              {formLines.length > 1 && (
                <Button size="small" onClick={() => removeFormLine(idx)}>
                  Sterge
                </Button>
              )}
                        </Box>
          ))}
          <Button onClick={addFormLine} size="small">
            + Linie
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Inchide</Button>
          <Button variant="contained" onClick={() => void submitCreate()}>
            Salveaza
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={supplierQuickOpen} onClose={() => setSupplierQuickOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Furnizor nou</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Nume" value={newSupplierName} onChange={(e) => setNewSupplierName(e.target.value)} sx={{ mt: 1, mb: 2 }} />
          <TextField fullWidth label="Telefon" value={newSupplierPhone} onChange={(e) => setNewSupplierPhone(e.target.value)} sx={{ mb: 2 }} />
          <TextField fullWidth label="Email" value={newSupplierEmail} onChange={(e) => setNewSupplierEmail(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSupplierQuickOpen(false)}>Anuleaza</Button>
          <Button variant="contained" onClick={() => void submitQuickSupplier()}>
            Salveaza
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!editOrder} onClose={() => setEditOrder(null)} maxWidth="md" fullWidth>
        <DialogTitle>Editeaza comanda {editOrder?.orderNumber}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Furnizor</InputLabel>
              <Select value={formSupplierId} label="Furnizor" onChange={(e) => setFormSupplierId(e.target.value)}>
                {suppliers.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField fullWidth label="Locatie" value={formLocation} onChange={(e) => setFormLocation(e.target.value)} sx={{ mb: 2 }} />
            <TextField fullWidth label="Note" value={formNotes} onChange={(e) => setFormNotes(e.target.value)} multiline minRows={2} sx={{ mb: 2 }} />
            {formLines.map((line, idx) => (
              <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <FormControl sx={{ minWidth: 220 }} size="small">
                  <InputLabel>Produs</InputLabel>
                  <Select label="Produs" value={line.productId} onChange={(e) => setLine(idx, { productId: e.target.value })}>
                    {products.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                          size="small"
                  label="Cantitate"
                  type="number"
                  value={line.quantityOrdered}
                  onChange={(e) => setLine(idx, { quantityOrdered: parseFloat(e.target.value) || 0 })}
                  sx={{ width: 110 }}
                />
                <TextField
                  size="small"
                  label="Pret unitar"
                  type="number"
                  value={line.unitPrice}
                  onChange={(e) => setLine(idx, { unitPrice: parseFloat(e.target.value) || 0 })}
                  sx={{ width: 120 }}
                />
                {formLines.length > 1 && (
                  <Button size="small" onClick={() => removeFormLine(idx)}>
                    Sterge
                  </Button>
                )}
                        </Box>
            ))}
            <Button onClick={addFormLine} size="small">
              + Linie
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOrder(null)}>Anuleaza</Button>
          <Button variant="contained" onClick={() => void submitEdit()}>
            Salveaza
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!viewOrder} onClose={() => setViewOrder(null)} maxWidth="md" fullWidth>
        <DialogTitle>Comanda {viewOrder?.orderNumber}</DialogTitle>
        <DialogContent>
          {viewOrder && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Furnizor: {viewOrder.supplierName}
                            </Typography>
              <Typography variant="body2" color="text.secondary">
                Status: {getStatusLabel(viewOrder.status)}
                            </Typography>
              <Divider sx={{ my: 2 }} />
              {viewOrder.items.map((i) => (
                <Typography key={i.id} variant="body2">
                  {i.productName}: comandat {i.quantityOrdered}, receptionat {i.quantityReceived}, pret {i.unitPrice}
                        </Typography>
              ))}
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                Atasamente
                        </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                PDF, Office, imagini (max 10MB). Incarcarea se face pe server; stergerea elimina si fisierul din storage.
                        </Typography>
              <input
                ref={attachmentFileRef}
                type="file"
                hidden
                accept=".pdf,.doc,.docx,.csv,.xls,.xlsx,image/jpeg,image/png,image/webp"
                onChange={(ev) => void onAttachmentFileChange(ev)}
              />
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AttachFileIcon />}
                  disabled={viewOrder.status === 'cancelled'}
                  onClick={onPickAttachment}
                >
                  Incarca fisier
                </Button>
                {attachmentsLoading && <Typography variant="caption">Se incarca lista…</Typography>}
                        </Box>
              {poAttachments.length === 0 && !attachmentsLoading ? (
                <Typography variant="body2" color="text.secondary">
                  Nu exista atasamente.
                </Typography>
              ) : (
          <TableContainer>
                  <Table size="small">
              <TableHead>
                <TableRow>
                        <TableCell>Fisier</TableCell>
                        <TableCell>Tip</TableCell>
                        <TableCell>Dimensiune</TableCell>
                        <TableCell>Data</TableCell>
                        <TableCell align="right">Actiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                      {poAttachments.map((a) => (
                        <TableRow key={a.id}>
                      <TableCell>
                            <Button href={resolveAttachmentHref(a)} target="_blank" rel="noopener noreferrer" size="small">
                              {a.fileName}
                            </Button>
                      </TableCell>
                          <TableCell>{a.mimeType}</TableCell>
                          <TableCell>{(a.sizeBytes / 1024).toFixed(1)} KB</TableCell>
                          <TableCell>{formatDateOnly(a.createdAt)}</TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => void onDeleteAttachment(a.id)} aria-label="Sterge atasament">
                              <DeleteOutlineIcon />
                            </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOrder(null)}>Inchide</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!receiveOrder} onClose={() => setReceiveOrder(null)} maxWidth="md" fullWidth>
        <DialogTitle>Receptie: {receiveOrder?.orderNumber}</DialogTitle>
        <DialogContent>
          {receiveOrder &&
            receiveOrder.items
              .filter((it) => it.quantityOrdered - it.quantityReceived > 0)
              .map((it) => {
                const st = receiveLines[it.id] || { qty: '0', batch: '', exp: '', cost: '0' };
                const rem = it.quantityOrdered - it.quantityReceived;
                return (
                  <Box key={it.id} sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Typography fontWeight={600}>
                      {it.productName} (ramas: {rem})
          </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                      <TextField
                        size="small"
                        label="Cantitate"
                        value={st.qty}
                        onChange={(e) =>
                          setReceiveLines((prev) => ({ ...prev, [it.id]: { ...st, qty: e.target.value } }))
                        }
                      />
                      <TextField
                        size="small"
                        label="Lot"
                        value={st.batch}
                        onChange={(e) =>
                          setReceiveLines((prev) => ({ ...prev, [it.id]: { ...st, batch: e.target.value } }))
                        }
                      />
                      <TextField
                        size="small"
                        label="Expirare"
                        placeholder="YYYY-MM-DD"
                        value={st.exp}
                        onChange={(e) =>
                          setReceiveLines((prev) => ({ ...prev, [it.id]: { ...st, exp: e.target.value } }))
                        }
                      />
                      <TextField
                        size="small"
                        label="Cost unitar"
                        value={st.cost}
                        onChange={(e) =>
                          setReceiveLines((prev) => ({ ...prev, [it.id]: { ...st, cost: e.target.value } }))
                        }
                      />
                    </Box>
                  </Box>
                );
              })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReceiveOrder(null)}>Anuleaza</Button>
          <Button variant="contained" onClick={() => void submitReceive()}>
            Confirma receptia
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PurchaseOrdersManagement;
